import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Shield, Upload, CheckCircle, AlertCircle, Clock,
  FileText, User, Camera, ArrowRight, Loader2, Info
} from 'lucide-react';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../hooks/useAuth';
import { useKYC } from '../hooks/useKYC';


const STEPS = [
  { id: 1, label: 'Informations', icon: User },
  { id: 2, label: 'Documents', icon: FileText },
  { id: 3, label: 'Confirmation', icon: CheckCircle },
];

export default function KYCVerification() {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const { submitKYC, loading } = useKYC();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [idDoc, setIdDoc] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [idPreview, setIdPreview] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // If already verified, redirect
  useEffect(() => {
    if (user?.kyc_status === 'verified') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleFileChange = (field, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      if (field === 'id') {
        setIdDoc(file);
        setIdPreview(e.target.result);
      } else {
        setSelfie(file);
        setSelfiePreview(e.target.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    setError('');
    if (!idDoc || !selfie) {
      setError(t('kyc.upload_both_docs'));
      return;
    }
    const formData = new FormData();
    formData.append('id_document', idDoc);
    formData.append('selfie_verification', selfie);

    const res = await submitKYC(formData);
    if (res.success) {
      setSubmitted(true);
      if (refreshUser) refreshUser();
    } else {
      setError(res.message || t('kyc.submit_error'));
    }
  };

  const kycStatus = user?.kyc_status;

  return (
    <div className="flex flex-col min-h-screen bg-slate-950 text-slate-200 font-inter">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <main className="flex-1 p-6 md:p-10 overflow-y-auto custom-scrollbar relative">
          <div className="absolute top-0 left-0 w-full h-full bg-indigo-500/5 blur-[150px] -z-10 pointer-events-none" />

          <div className="max-w-3xl mx-auto space-y-8 animate-fade-in">

            {/* Header */}
            <div className="space-y-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 border border-white/10 rounded-full">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">{t('kyc.identity_verification')}</span>
              </div>
              <h1 className="text-3xl font-black text-white tracking-tight">
                KYC — <span className="text-indigo-400">{t('kyc.verification_center')}</span>
              </h1>
              <p className="text-slate-400">{t('kyc.complete_verification_desc')}</p>
            </div>

            {/* Status Card */}
            {kycStatus === 'pending' && !submitted && (
              <div className="glass-card p-6 border-amber-500/20 bg-amber-500/5 flex items-center gap-4">
                <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                  <Clock size={24} className="text-amber-400 animate-pulse" />
                </div>
                <div>
                  <p className="font-black text-white">{t('kyc.folder_in_progress')}</p>
                  <p className="text-sm text-amber-300/70 mt-1">{t('kyc.folder_submitted_desc')}</p>
                </div>
              </div>
            )}

            {kycStatus === 'rejected' && !submitted && (
              <div className="glass-card p-6 border-rose-500/20 bg-rose-500/5 flex items-center gap-4">
                <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20">
                  <AlertCircle size={24} className="text-rose-400" />
                </div>
                <div>
                  <p className="font-black text-white">{t('kyc.folder_rejected')}</p>
                  <p className="text-sm text-rose-300/70 mt-1">{t('kyc.folder_rejected_desc')}</p>
                </div>
              </div>
            )}

            {/* Success state */}
            {submitted && (
              <div className="glass-card p-10 border-emerald-500/20 bg-emerald-500/5 flex flex-col items-center gap-6 text-center">
                <div className="p-6 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                  <CheckCircle size={48} className="text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-black text-white mb-2">{t('kyc.folder_success')}</p>
                  <p className="text-slate-400 max-w-md">{t('kyc.folder_success_desc')}</p>
                </div>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="flex items-center gap-2 px-6 py-3 bg-indigo-500 rounded-xl text-white font-bold hover:bg-indigo-600 transition-all"
                >
                  Retour au Dashboard <ArrowRight size={16} />
                </button>
              </div>
            )}

            {/* Form — only show if not submitted and not already pending */}
            {!submitted && kycStatus !== 'pending' && (
              <>
                {/* Step Progress */}
                <div className="flex items-center gap-2">
                  {STEPS.map((s, i) => (
                    <React.Fragment key={s.id}>
                      <div className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${
                        step === s.id ? 'bg-indigo-500 text-white' : step > s.id ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/5 text-slate-500'
                      }`}>
                        {step > s.id ? <CheckCircle size={12} /> : <s.icon size={12} />}
                        {s.label}
                      </div>
                      {i < STEPS.length - 1 && <div className="flex-1 h-px bg-white/5" />}
                    </React.Fragment>
                  ))}
                </div>

                {/* Step 1: Info */}
                {step === 1 && (
                  <div className="glass-card p-8 border-white/5 space-y-6">
                    <h2 className="text-lg font-black text-white uppercase tracking-tight">Informations requises</h2>
                    <div className="space-y-4">
                      {[
                        { icon: FileText, title: t('kyc.id_doc_front'), desc: t('kyc.id_doc_desc') },
                        { icon: Camera, title: t('kyc.selfie_doc'), desc: t('kyc.selfie_desc') },
                        { icon: Info, title: t('kyc.accepted_format'), desc: t('kyc.format_desc') },
                        { icon: Shield, title: t('kyc.data_security'), desc: t('kyc.security_desc') },
                      ].map((item, i) => (
                        <div key={i} className="flex items-start gap-4 p-4 bg-white/3 rounded-xl border border-white/5">
                          <div className="p-2 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex-shrink-0">
                            <item.icon size={16} className="text-indigo-400" />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-white">{item.title}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => setStep(2)}
                      className="w-full flex items-center justify-center gap-2 py-3 bg-indigo-500 rounded-xl text-white font-bold hover:bg-indigo-600 transition-all"
                    >
                      {t('kyc.start_verification')} <ArrowRight size={16} />
                    </button>
                  </div>
                )}

                {/* Step 2: Upload */}
                {step === 2 && (
                  <div className="glass-card p-8 border-white/5 space-y-6">
                    <h2 className="text-lg font-black text-white uppercase tracking-tight">{t('kyc.upload_documents')}</h2>

                    {/* ID Document */}
                    <div className="space-y-3">
                      <label className="text-sm font-bold text-slate-300">{t('kyc.id_document_req')}</label>
                      <label className={`flex flex-col items-center justify-center gap-4 p-8 rounded-2xl border-2 border-dashed cursor-pointer transition-all ${
                        idPreview ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-white/10 hover:border-indigo-500/40 hover:bg-indigo-500/5'
                      }`}>
                        {idPreview ? (
                          <div className="space-y-2 text-center">
                            <img src={idPreview} alt="ID" className="max-h-40 mx-auto rounded-xl object-cover border border-white/10" />
                            <p className="text-xs text-emerald-400 font-bold flex items-center gap-1 justify-center">
                              <CheckCircle size={12} /> {t('kyc.doc_uploaded')}
                            </p>
                          </div>
                        ) : (
                          <>
                            <div className="p-4 rounded-full bg-white/5">
                              <Upload size={28} className="text-slate-500" />
                            </div>
                            <div className="text-center">
                              <p className="text-sm font-bold text-slate-300">{t('kyc.drag_drop')}</p>
                              <p className="text-xs text-slate-500 mt-1">JPG, PNG, PDF — Max 5MB</p>
                            </div>
                          </>
                        )}
                        <input type="file" className="hidden" accept="image/*,.pdf" onChange={(e) => handleFileChange('id', e.target.files[0])} />
                      </label>
                    </div>

                    {/* Selfie */}
                    <div className="space-y-3">
                      <label className="text-sm font-bold text-slate-300">{t('kyc.selfie_req')}</label>
                      <label className={`flex flex-col items-center justify-center gap-4 p-8 rounded-2xl border-2 border-dashed cursor-pointer transition-all ${
                        selfiePreview ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-white/10 hover:border-indigo-500/40 hover:bg-indigo-500/5'
                      }`}>
                        {selfiePreview ? (
                          <div className="space-y-2 text-center">
                            <img src={selfiePreview} alt="Selfie" className="max-h-40 mx-auto rounded-xl object-cover border border-white/10" />
                            <p className="text-xs text-emerald-400 font-bold flex items-center gap-1 justify-center">
                              <CheckCircle size={12} /> {t('kyc.selfie_uploaded')}
                            </p>
                          </div>
                        ) : (
                          <>
                            <div className="p-4 rounded-full bg-white/5">
                              <Camera size={28} className="text-slate-500" />
                            </div>
                            <div className="text-center">
                              <p className="text-sm font-bold text-slate-300">{t('kyc.drag_drop')}</p>
                              <p className="text-xs text-slate-500 mt-1">{t('kyc.hold_doc_visible')}</p>
                            </div>
                          </>
                        )}
                        <input type="file" className="hidden" accept="image/*" onChange={(e) => handleFileChange('selfie', e.target.files[0])} />
                      </label>
                    </div>

                    {error && (
                      <div className="flex items-center gap-2 p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl text-rose-400 text-sm">
                        <AlertCircle size={14} /> {error}
                      </div>
                    )}

                    <div className="flex gap-3">
                      <button onClick={() => setStep(1)} className="flex-1 py-3 rounded-xl border border-white/10 text-slate-400 font-bold hover:bg-white/5 transition-all text-sm">
                        Retour
                      </button>
                      <button
                        onClick={() => { if (idDoc && selfie) setStep(3); else setError(t('kyc.upload_both_docs')); }}
                        className="flex-1 flex items-center justify-center gap-2 py-3 bg-indigo-500 rounded-xl text-white font-bold hover:bg-indigo-600 transition-all text-sm"
                      >
                        Continuer <ArrowRight size={16} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 3: Confirm */}
                {step === 3 && (
                  <div className="glass-card p-8 border-white/5 space-y-6">
                    <h2 className="text-lg font-black text-white uppercase tracking-tight">Confirmer l'envoi</h2>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <p className="text-xs font-black text-slate-500 uppercase tracking-widest">{t('kyc.id_doc')}</p>
                        <img src={idPreview} alt="ID" className="w-full rounded-xl object-cover border border-white/10 max-h-48" />
                      </div>
                      <div className="space-y-2">
                        <p className="text-xs font-black text-slate-500 uppercase tracking-widest">Selfie</p>
                        <img src={selfiePreview} alt="Selfie" className="w-full rounded-xl object-cover border border-white/10 max-h-48" />
                      </div>
                    </div>

                    <div className="p-4 bg-indigo-500/5 border border-indigo-500/20 rounded-xl text-xs text-indigo-300 flex items-start gap-2">
                      <Shield size={14} className="flex-shrink-0 mt-0.5" />
                      {t('kyc.privacy_agreement')}
                    </div>

                    {error && (
                      <div className="flex items-center gap-2 p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl text-rose-400 text-sm">
                        <AlertCircle size={14} /> {error}
                      </div>
                    )}

                    <div className="flex gap-3">
                      <button onClick={() => setStep(2)} className="flex-1 py-3 rounded-xl border border-white/10 text-slate-400 font-bold hover:bg-white/5 transition-all text-sm">
                        Modifier
                      </button>
                      <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="flex-1 flex items-center justify-center gap-2 py-3 bg-emerald-500 rounded-xl text-white font-bold hover:bg-emerald-600 transition-all text-sm disabled:opacity-60"
                      >
                        {loading ? <><Loader2 size={16} className="animate-spin" /> {t('kyc.sending')}</> : <><CheckCircle size={16} /> {t('kyc.submit_folder')}</>}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
